import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  RiFileTextLine,
  RiCheckboxCircleLine,
  RiTimeLine,
  RiArrowUpLine,
  RiArrowDownLine,
  RiBuilding2Line,
  RiBarChartLine,
  RiTeamLine,
  RiCalendarLine,
  RiPriceTag3Line,
  RiInboxLine,
  RiRefreshLine,
  RiNewspaperLine,
  RiUserLine,
  RiShieldUserLine,
  RiPhoneLine,
  RiMailLine,
  RiMapPinLine,
} from "react-icons/ri";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { PiSparkleLight } from "react-icons/pi";
import welcomeimg from "../../Assets/Crowns/student.gif";

const BASE_URL = process.env.REACT_APP_API_URL;

const storedData = JSON.parse(localStorage.getItem("Nexgen-Admin-Data"));
const id = storedData?._id;
const name = storedData?.name;
const firstName = name?.split(" ")[0];

const Spinner = () => (
  <AiOutlineLoading3Quarters className="w-5 h-5 animate-spin" />
);

const StatCard = ({ icon: Icon, label, value, subtext, trend, accent }) => (
  <div
    className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md ${accent}`}
  >
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div
          className={`w-12 h-12 rounded-xl flex items-center justify-center ${
            accent === "border-emerald-100"
              ? "bg-emerald-100"
              : accent === "border-amber-100"
                ? "bg-amber-100"
                : accent === "border-blue-100"
                  ? "bg-blue-100"
                  : accent === "border-purple-100"
                    ? "bg-purple-100"
                    : accent === "border-orange-100"
                      ? "bg-orange-100"
                      : accent === "border-rose-100"
                        ? "bg-rose-100"
                        : "bg-violet-100"
          }`}
        >
          <Icon
            className={`w-6 h-6 ${
              accent === "border-emerald-100"
                ? "text-emerald-600"
                : accent === "border-amber-100"
                  ? "text-amber-600"
                  : accent === "border-blue-100"
                    ? "text-blue-600"
                    : accent === "border-purple-100"
                      ? "text-purple-600"
                      : accent === "border-orange-100"
                        ? "text-orange-600"
                        : accent === "border-rose-100"
                          ? "text-rose-600"
                          : "text-violet-600"
            }`}
          />
        </div>
        {trend && (
          <div
            className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${
              trend > 0
                ? "bg-emerald-100 text-emerald-700"
                : "bg-red-100 text-red-700"
            }`}
          >
            {trend > 0 ? (
              <RiArrowUpLine className="w-3 h-3" />
            ) : (
              <RiArrowDownLine className="w-3 h-3" />
            )}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-1">
        {label}
      </p>
      <p className="text-3xl font-bold text-slate-800 mb-1">{value}</p>
      {subtext && <p className="text-xs text-slate-500">{subtext}</p>}
    </div>
  </div>
);

// Welcome banner section
const WelcomeBanner = ({ adminName = "Admin" }) => (
  <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-blue-600 via-cyan-500 to-emerald-500 p-8 mb-8 shadow-lg">
    <div className="absolute -top-20 -right-20 w-40 h-40 bg-white/10 rounded-full blur-3xl animate-pulse" />
    <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/10 rounded-full blur-3xl animate-pulse delay-700" />

    <div className="relative flex items-center gap-6">
      <div className="w-28 h-28 rounded-full bg-white/90 backdrop-blur-sm border-4 border-white shadow-xl overflow-hidden flex items-center justify-center flex-shrink-0">
        <img src={welcomeimg} alt="" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <h1 className="text-4xl font-bold text-white">
            Welcome Back, {firstName}!
          </h1>
          <span className="text-3xl">👋</span>
        </div>
        <p className="text-white/90 text-lg flex items-center gap-2">
          Here's a quick snapshot of your advertising platform's performance.
          <span>🚀</span>
        </p>
      </div>
    </div>
  </div>
);

// Release Orders Section
const ReleaseOrdersSection = ({ data, loading }) => {
  const [expandedRO, setExpandedRO] = useState(false);

  const totalROs = data?.length || 0;
  const pendingROs =
    data?.filter((ro) =>
      ro.variants?.every(
        (v) => !v.invoiceStatus || v.invoiceStatus === "pending",
      ),
    ).length || 0;
  const completeROs = totalROs - pendingROs;

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-blue-100 shadow-sm p-8">
        <div className="flex items-center justify-center gap-3 text-blue-600">
          <Spinner />
          <span>Loading release orders...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-blue-100 p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <RiFileTextLine className="w-5 h-5 text-blue-600" />
            </div>
            <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full">
              Total
            </span>
          </div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1">
            Total Release Orders
          </p>
          <p className="text-4xl font-bold text-slate-800">{totalROs}</p>
          <p className="text-xs text-slate-400 mt-2">All ROs in the system</p>
        </div>

        <div className="bg-white rounded-2xl border border-amber-100 p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <RiTimeLine className="w-5 h-5 text-amber-600" />
            </div>
            <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full">
              Pending
            </span>
          </div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1">
            Pending Invoice
          </p>
          <p className="text-4xl font-bold text-amber-600">{pendingROs}</p>
          <p className="text-xs text-slate-400 mt-2">
            Awaiting invoice creation
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-emerald-100 p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <RiCheckboxCircleLine className="w-5 h-5 text-emerald-600" />
            </div>
            <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
              Complete
            </span>
          </div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1">
            Invoice Complete
          </p>
          <p className="text-4xl font-bold text-emerald-600">{completeROs}</p>
          <p className="text-xs text-slate-400 mt-2">
            {Math.round((completeROs / totalROs) * 100) || 0}% complete
          </p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-blue-50 bg-blue-50/30 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RiFileTextLine className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-slate-800">
              Release Orders Overview
            </h3>
          </div>
          <button
            onClick={() => setExpandedRO(!expandedRO)}
            className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition"
          >
            {expandedRO ? "Hide Details" : "Show Details"}
          </button>
        </div>

        {expandedRO && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-blue-50 bg-blue-50/50">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                    Variants
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody>
                {data?.slice(0, 5).map((ro, idx) => {
                  const allPending = ro.variants?.every(
                    (v) => !v.invoiceStatus || v.invoiceStatus === "pending",
                  );

                  return (
                    <tr
                      key={idx}
                      className="border-b border-blue-50 hover:bg-blue-50/30 transition-colors"
                    >
                      <td className="px-6 py-3 text-slate-700">
                        {ro.client?.bankName || "—"}
                      </td>
                      <td className="px-6 py-3">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 border border-blue-200">
                          {ro.variants?.length || 0} variant
                          {(ro.variants?.length || 0) !== 1 ? "s" : ""}
                        </span>
                      </td>
                      <td className="px-6 py-3">
                        {allPending ? (
                          <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                            <RiTimeLine className="w-3 h-3" />
                            Pending
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                            <RiCheckboxCircleLine className="w-3 h-3" />
                            Complete
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-slate-500">
                        {ro.date
                          ? new Date(ro.date).toLocaleDateString("en-IN")
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Invoices Section
const InvoicesSection = ({ data, loading }) => {
  const [expandedInv, setExpandedInv] = useState(false);

  const totalInvoices = data?.length || 0;
  const totalAmount =
    data?.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0) || 0;

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-violet-100 shadow-sm p-8">
        <div className="flex items-center justify-center gap-3 text-violet-600">
          <Spinner />
          <span>Loading invoices...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-violet-100 p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
              <RiFileTextLine className="w-5 h-5 text-violet-600" />
            </div>
            <span className="text-xs font-bold text-violet-600 bg-violet-50 px-2.5 py-1 rounded-full">
              Total
            </span>
          </div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1">
            Total Invoices
          </p>
          <p className="text-4xl font-bold text-slate-800">{totalInvoices}</p>
          <p className="text-xs text-slate-400 mt-2">
            {totalInvoices === 1 ? "invoice" : "invoices"} created
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-indigo-100 p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <RiPriceTag3Line className="w-5 h-5 text-indigo-600" />
            </div>
            <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full">
              Revenue
            </span>
          </div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1">
            Total Amount
          </p>
          <p className="text-3xl font-bold text-slate-800">
            ₹{totalAmount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
          </p>
          <p className="text-xs text-slate-400 mt-2">
            Average: ₹
            {(totalAmount / (totalInvoices || 1)).toLocaleString("en-IN", {
              maximumFractionDigits: 0,
            })}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-violet-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-violet-50 bg-violet-50/30 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RiFileTextLine className="w-5 h-5 text-violet-600" />
            <h3 className="font-semibold text-slate-800">Recent Invoices</h3>
          </div>
          <button
            onClick={() => setExpandedInv(!expandedInv)}
            className="text-xs font-semibold text-violet-600 hover:text-violet-800 transition"
          >
            {expandedInv ? "Hide Details" : "Show Details"}
          </button>
        </div>

        {expandedInv && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-violet-50 bg-violet-50/50">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                    Invoice No.
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                    Bill To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody>
                {data?.slice(0, 5).map((inv, idx) => (
                  <tr
                    key={idx}
                    className="border-b border-violet-50 hover:bg-violet-50/30 transition-colors"
                  >
                    <td className="px-6 py-3 font-mono font-semibold text-violet-700">
                      {inv.invoiceNumber}
                    </td>
                    <td className="px-6 py-3 text-slate-700">
                      {inv.billTo?.company ||
                        inv.releaseOrder?.client?.bankName ||
                        "—"}
                    </td>
                    <td className="px-6 py-3 font-bold text-indigo-700">
                      ₹
                      {(inv.totalAmount || 0).toLocaleString("en-IN", {
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="px-6 py-3 text-slate-500">
                      {inv.date
                        ? new Date(inv.date).toLocaleDateString("en-IN")
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const MediaSection = ({ data, loading }) => {
  const [expanded, setExpanded] = useState(false);

  const total = data?.length || 0;

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-orange-100 shadow-sm p-8">
        <div className="flex items-center justify-center gap-3 text-orange-600">
          <Spinner />
          <span>Loading media...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-orange-100 p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <RiNewspaperLine className="w-5 h-5 text-orange-600" />
            </div>
            <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2.5 py-1 rounded-full">
              Total
            </span>
          </div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1">
            Total Media
          </p>
          <p className="text-4xl font-bold text-slate-800">{total}</p>
          <p className="text-xs text-slate-400 mt-2">
            {total === 1 ? "media organisation" : "media organisations"}{" "}
            registered
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-amber-100 p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <RiBuilding2Line className="w-5 h-5 text-amber-600" />
            </div>
            <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full">
              Unique
            </span>
          </div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1">
            Unique Departments
          </p>
          <p className="text-4xl font-bold text-slate-800">
            {new Set(data?.map((m) => m.departmentOffice).filter(Boolean))
              .size || 0}
          </p>
          <p className="text-xs text-slate-400 mt-2">Across all media orgs</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-orange-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-orange-50 bg-orange-50/30 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RiNewspaperLine className="w-5 h-5 text-orange-600" />
            <h3 className="font-semibold text-slate-800">Media Overview</h3>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs font-semibold text-orange-600 hover:text-orange-800 transition"
          >
            {expanded ? "Hide Details" : "Show Details"}
          </button>
        </div>

        {expanded && (
          <div className="overflow-x-auto">
            {data?.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                <RiInboxLine className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No media organisations found</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-orange-50 bg-orange-50/50">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                      Organisation
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                      Designation
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                      Phone
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data?.slice(0, 5).map((item, idx) => (
                    <tr
                      key={idx}
                      className="border-b border-orange-50 hover:bg-orange-50/30 transition-colors"
                    >
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-lg bg-orange-600 flex items-center justify-center text-white text-xs font-bold shadow-sm flex-shrink-0">
                            {(item.organisation || "?")[0].toUpperCase()}
                          </div>
                          <span className="font-semibold text-slate-800 whitespace-nowrap">
                            {item.organisation || "—"}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-3 text-slate-600 whitespace-nowrap">
                        {item.contactPerson || "—"}
                      </td>
                      <td className="px-6 py-3">
                        {item.designation ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
                            {item.designation}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-6 py-3 text-slate-500 whitespace-nowrap">
                        {item.email || "—"}
                      </td>
                      <td className="px-6 py-3 text-slate-500 whitespace-nowrap">
                        {item.phone || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const AgentsSection = ({ data, loading }) => {
  const [expanded, setExpanded] = useState(false);

  const total = data?.length || 0;
  const uniquePositions =
    new Set(data?.map((a) => a.position).filter(Boolean)).size || 0;

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-purple-100 shadow-sm p-8">
        <div className="flex items-center justify-center gap-3 text-purple-600">
          <Spinner />
          <span>Loading agents...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-purple-100 p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <RiTeamLine className="w-5 h-5 text-purple-600" />
            </div>
            <span className="text-xs font-bold text-purple-600 bg-purple-50 px-2.5 py-1 rounded-full">
              Total
            </span>
          </div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1">
            Total Agents
          </p>
          <p className="text-4xl font-bold text-slate-800">{total}</p>
          <p className="text-xs text-slate-400 mt-2">
            {total === 1 ? "agent" : "agents"} in the system
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-violet-100 p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
              <RiShieldUserLine className="w-5 h-5 text-violet-600" />
            </div>
            <span className="text-xs font-bold text-violet-600 bg-violet-50 px-2.5 py-1 rounded-full">
              Roles
            </span>
          </div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1">
            Unique Positions
          </p>
          <p className="text-4xl font-bold text-slate-800">{uniquePositions}</p>
          <p className="text-xs text-slate-400 mt-2">Distinct roles assigned</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-purple-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-purple-50 bg-purple-50/30 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RiTeamLine className="w-5 h-5 text-purple-600" />
            <h3 className="font-semibold text-slate-800">Agents Overview</h3>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs font-semibold text-purple-600 hover:text-purple-800 transition"
          >
            {expanded ? "Hide Details" : "Show Details"}
          </button>
        </div>

        {expanded && (
          <div className="overflow-x-auto">
            {data?.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                <RiInboxLine className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No agents found</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-purple-50 bg-purple-50/50">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                      Agent
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                      Position
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                      Phone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                      Address
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data?.slice(0, 5).map((agent, idx) => (
                    <tr
                      key={idx}
                      className="border-b border-purple-50 hover:bg-purple-50/30 transition-colors"
                    >
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-2.5">
                          {agent.profilePic ? (
                            <img
                              src={agent.profilePic}
                              alt={agent.name}
                              className="w-8 h-8 rounded-full object-contain border border-purple-100 shadow-sm flex-shrink-0"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white text-xs font-bold shadow-sm flex-shrink-0">
                              {(agent.name || "?")[0].toUpperCase()}
                            </div>
                          )}
                          <span className="font-semibold text-slate-800 whitespace-nowrap">
                            {agent.name || "—"}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-3">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                          {agent.position || "Agent"}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-slate-500 whitespace-nowrap">
                        {agent.number || "—"}
                      </td>
                      <td className="px-6 py-3 text-slate-500 whitespace-nowrap">
                        {agent.email || "—"}
                      </td>
                      <td className="px-6 py-3 text-slate-500 max-w-[160px] truncate">
                        {agent.address || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const StaffSection = ({ data, loading }) => {
  const [expanded, setExpanded] = useState(false);

  const total = data?.length || 0;
  const uniquePositions =
    new Set(data?.map((s) => s.position).filter(Boolean)).size || 0;

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-rose-100 shadow-sm p-8">
        <div className="flex items-center justify-center gap-3 text-rose-600">
          <Spinner />
          <span>Loading staff...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-rose-100 p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center">
              <RiUserLine className="w-5 h-5 text-rose-600" />
            </div>
            <span className="text-xs font-bold text-rose-600 bg-rose-50 px-2.5 py-1 rounded-full">
              Total
            </span>
          </div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1">
            Total Staff
          </p>
          <p className="text-4xl font-bold text-slate-800">{total}</p>
          <p className="text-xs text-slate-400 mt-2">
            {total === 1 ? "staff member" : "staff members"} active
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-pink-100 p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-lg bg-pink-100 flex items-center justify-center">
              <RiShieldUserLine className="w-5 h-5 text-pink-600" />
            </div>
            <span className="text-xs font-bold text-pink-600 bg-pink-50 px-2.5 py-1 rounded-full">
              Roles
            </span>
          </div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-1">
            Unique Positions
          </p>
          <p className="text-4xl font-bold text-slate-800">{uniquePositions}</p>
          <p className="text-xs text-slate-400 mt-2">Distinct roles assigned</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-rose-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-rose-50 bg-rose-50/30 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RiUserLine className="w-5 h-5 text-rose-600" />
            <h3 className="font-semibold text-slate-800">Staff Overview</h3>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs font-semibold text-rose-600 hover:text-rose-800 transition"
          >
            {expanded ? "Hide Details" : "Show Details"}
          </button>
        </div>

        {expanded && (
          <div className="overflow-x-auto">
            {data?.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                <RiInboxLine className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No staff members found</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-rose-50 bg-rose-50/50">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                      Staff Member
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                      Position
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                      Phone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                      Address
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data?.slice(0, 5).map((member, idx) => (
                    <tr
                      key={idx}
                      className="border-b border-rose-50 hover:bg-rose-50/30 transition-colors"
                    >
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-2.5">
                          {member.profilePic ? (
                            <img
                              src={member.profilePic}
                              alt={member.name}
                              className="w-8 h-8 rounded-full object-contain border border-rose-100 shadow-sm flex-shrink-0"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-rose-500 flex items-center justify-center text-white text-xs font-bold shadow-sm flex-shrink-0">
                              {(member.name || "?")[0].toUpperCase()}
                            </div>
                          )}
                          <span className="font-semibold text-slate-800 whitespace-nowrap">
                            {member.name || "—"}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-3">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose-50 text-rose-700 border border-rose-200">
                          {member.position || "Staff"}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-slate-500 whitespace-nowrap">
                        {member.number || "—"}
                      </td>
                      <td className="px-6 py-3 text-slate-500 whitespace-nowrap">
                        {member.email || "—"}
                      </td>
                      <td className="px-6 py-3 text-slate-500 max-w-[160px] truncate">
                        {member.address || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default function DashboardPage() {
  const [releaseOrders, setReleaseOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [mediaList, setMediaList] = useState([]);
  const [agents, setAgents] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [roRes, invRes, mediaRes, agentRes, staffRes] =
          await Promise.allSettled([
            axios.get(`${BASE_URL}/releaseOrder/all`),
            axios.get(`${BASE_URL}/invoice/all`),
            axios.get(`${BASE_URL}/media/get-all`),
            axios.get(`${BASE_URL}/agent/all-agents`),
            axios.get(`${BASE_URL}/admin/all-users`),
          ]);

        if (roRes.status === "fulfilled")
          setReleaseOrders(roRes.value?.data?.data || roRes.value?.data || []);

        if (invRes.status === "fulfilled")
          setInvoices(invRes.value?.data?.data || invRes.value?.data || []);

        if (mediaRes.status === "fulfilled")
          setMediaList(
            mediaRes.value?.data?.data || mediaRes.value?.data || [],
          );

        if (agentRes.status === "fulfilled")
          setAgents(agentRes.value?.data?.data || agentRes.value?.data || []);

        if (staffRes.status === "fulfilled") {
          const allUsers =
            staffRes.value?.data?.data || staffRes.value?.data || [];
          setStaffList(allUsers.filter((u) => u.userType === "staff"));
        }

        setError(null);
      } catch (err) {
        setError("Failed to load dashboard data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50">
      {/* Main Content */}
      <main className="px-6 py-4 w-full mx-auto space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
            ⚠️ {error}
          </div>
        )}

        <WelcomeBanner />

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatCard
            icon={RiFileTextLine}
            label="Release Orders"
            value={releaseOrders.length}
            accent="border-blue-100"
          />
          <StatCard
            icon={RiTeamLine}
            label="Clients"
            value={new Set(releaseOrders.map((ro) => ro.client?._id)).size}
            accent="border-purple-100"
          />
          <StatCard
            icon={RiFileTextLine}
            label="Invoices"
            value={invoices.length}
            accent="border-violet-100"
          />

          <StatCard
            icon={RiNewspaperLine}
            label="Media"
            value={mediaList.length}
            accent="border-orange-100"
          />
          <StatCard
            icon={RiTeamLine}
            label="Agents"
            value={agents.length}
            accent="border-purple-100"
          />
          <StatCard
            icon={RiUserLine}
            label="Staff"
            value={staffList.length}
            accent="border-rose-100"
          />
        </div>

        <div>
          <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-2">
            <RiFileTextLine className="w-6 h-6 text-blue-600" />
            Release Orders
          </h2>
          <ReleaseOrdersSection data={releaseOrders} loading={loading} />
        </div>

        {/* Invoices Section */}
        <div>
          <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-2">
            <RiFileTextLine className="w-6 h-6 text-violet-600" />
            Invoices
          </h2>
          <InvoicesSection data={invoices} loading={loading} />
        </div>

        <div>
          <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-2">
            <RiNewspaperLine className="w-6 h-6 text-orange-600" />
            Media
          </h2>
          <MediaSection data={mediaList} loading={loading} />
        </div>

        <div>
          <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-2">
            <RiTeamLine className="w-6 h-6 text-purple-600" />
            Agents
          </h2>
          <AgentsSection data={agents} loading={loading} />
        </div>

        {/* Staff Section */}
        <div>
          <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-2">
            <RiUserLine className="w-6 h-6 text-rose-600" />
            Staff
          </h2>
          <StaffSection data={staffList} loading={loading} />
        </div>
      </main>
    </div>
  );
}
